import React, { useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { BrowserRouter, Switch, Route } from "react-router-dom";
import { useQuery } from "@apollo/react-hooks";
import gql from "graphql-tag";
import { createTheme } from "@material-ui/core/styles";
import { ThemeProvider } from "@material-ui/styles";
import AppContextProvider, { AppContext } from "./Context";
import Header from "./Common/Header";
import Menu from "./Common/menu";
import NewBookings from "./Bookings";
import History from "./History";
import WeekView from "./WeekView";
import Courses from "./Courses";
import Home from "./Home";
import RequireAuth from "./Common/requireAuth";
import CallbackPage from "./Common/callback";
import SilentRenew from "./Common/silentRenew";
import BookingModal from "./occasion/bookingModal";
import Toast from "./Common/toast";
import Settings from "./Common/settings";
import { makeStyles } from "@material-ui/styles";
import UserManagerProvider from "./Context/userManagerContext";
import { getTheme } from "./Common/theme";
import BottomNav from "./Common/bottomNav";
import Unpaid from "./Common/unpaid";
import SetBackUrl from "./Common/SetBackUrl";
import { STATE_CULTURE } from "./Actions/stateActions";
import NewBookingModal from "./Bookings/NewBookingModal";
import Markdown from "react-markdown";
import { useMediaQuery } from "@mantine/hooks";

const useStyles = makeStyles((theme) => ({
  root: {
    height: `${theme.height}px`,
    // overflow: "hidden",
    position: "relative",
    display: "flex",
    "@media(max-width: 600px)": {
      display: "block"
    }
    // "& ::-webkit-scrollbar": {
    //   width: "10px",
    // },
    // "& ::-webkit-scrollbar-track": {
    //   "-webkit-box-shadow": "inset 0 0 6px rgba(0,0,0,0.3)",
    // },
    // "& ::-webkit-scrollbar-thumb": {
    //   backgroundColor: theme.app.scrollbar,
    //   outline: `1px solid ${theme.app.borderColor}`,
    // },
  },
  workspaceContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    background: theme.app.background[0],
    width: "100%",
    position: "relative",
    paddingTop: "64px",
    overflowY: "auto",
    "@media(max-width: 600px)": {
      // height: "calc(100% - 56px)",
    }
  },
  footer: {
    width: "100%",
    alignSelf: "end",
    height: "fit-content",
    background: theme.app.background[1],
    color: theme.app.labelColor
  }
}));

export const MY_BOOKINGS = gql`
  query myBookings($position: String) {
    mybookings(position: $position) {
      id
      status
      isQueue
      queueSlot
      isCancelled
      cancelledAt
      isConfirmed
      confirmedAt
      isPaid
      canCancel
      remainingPaymentInclVat
      lastPaymentTime
      activity {
        id
        name
        infoText
        color
        occasions {
          at
        }
        location {
          id
          name
        }
      }
      occasion {
        id
        at
        duration
        maxQueueSlots
      }
      participants {
        id
        name
      }
      responsible {
        id
      }
    }
    whoami {
      photo
      name
    }
    manages {
      id
      name
      email
      civicRegistrationNumber
      photo
    }
  }
`;

const theme = (myTheme) =>
  createTheme({
    app: myTheme,
    height: myTheme.height,
    palette: {
      type: myTheme.themeType,
      primary: {
        light: myTheme.primaryColors.light,
        main: myTheme.primaryColors.main,
        dark: myTheme.primaryColors.dark
      },
      secondary: {
        light: myTheme.secondaryColors.light,
        main: myTheme.secondaryColors.main,
        dark: myTheme.secondaryColors.dark
      },
      background: {
        paper: myTheme.background[1],
        default: myTheme.background[0]
      }
    }
  });

const Workspace = ({ classes, mybookings, whoami, footerText }) => {
  const mobile = useMediaQuery("(max-width: 768px)");

  return (
    <div className={classes.workspaceContainer} id="main-page-content">
      <Switch>
        <Route path="/home" component={Home} />
        <Route path="/bookings">
          <RequireAuth>
            <NewBookings mybookings={mybookings} whoami={whoami} />
          </RequireAuth>
        </Route>
        <Route path="/history">
          <RequireAuth>
            <History />
          </RequireAuth>
        </Route>
        <Route path="/courses" component={Courses} />
        <Route path="/week/location/:location" exactly component={WeekView} />
        <Route path="/week/:year/:week" exactly component={WeekView} />
        <Route path="/week" component={WeekView} />
        <Route component={Home} />
      </Switch>
      <footer
        className={classes.footer}
        style={{ marginBottom: mobile && "55px" }}
      >
        <Markdown
          source={footerText}
          escapeHtml={false}
          skipHtml={false}
          className="footer-text-container"
        />
      </footer>
    </div>
  );
};

const Content = () => {
  const classes = useStyles();
  const auth = useSelector(
    (state) => state.oidc.user !== null && !state.oidc.user.expired
  );
  const { culture, language } = useSelector((state) => ({
    culture: state.cookies[STATE_CULTURE],
    language: state.language
  }));

  const { state } = useContext(AppContext);
  const { footerText } = state;

  const { data, loading, refetch } = useQuery(MY_BOOKINGS, {
    pollInterval: 60000
  });
  if (loading) return <div>loading</div>;
  if ((!data || !data.mybookings || data.whoami.name === "NA") && auth) {
    refetch();
  }

  const mybookings = data.mybookings;
  return (
    <>
      <BookingModal />
      <NewBookingModal
        mybookings={mybookings}
        whoami={data.whoami}
        culture={culture}
        language={language}
      />
      <div className={classes.root}>
        <Header whoami={data.whoami} />
        <Menu mybookings={mybookings} />
        <Workspace
          classes={classes}
          mybookings={mybookings}
          whoami={data.whoami}
          footerText={footerText}
        />
        <BottomNav mybookings={mybookings} />
        <Settings />
        <Unpaid mybookings={mybookings} culture={culture} />
        <Toast />
      </div>
    </>
  );
};

const Routes = (props) => {
  const { userManager } = props;
  const [prevHeight, setPrevHeight] = useState(window.innerHeight);
  const selectedTheme = useSelector((state) => state.cookies.theme);
  const myTheme = getTheme(selectedTheme);
  window.addEventListener("resize", () => {
    if (window.innerHeight !== prevHeight) {
      setPrevHeight(window.innerHeight);
    }
  });

  const { language, culture } = useSelector((state) => ({
    language: state.language,
    culture: state.cookies[STATE_CULTURE]
  }));

  // Set HTML language
  useEffect(() => {
    document.documentElement.lang = culture.split("-")[0];
  }, [culture]);

  useEffect(() => {
    document.title = `${language?.sideTitle ?? document.title}`;
  }, [language]);

  return (
    <AppContextProvider>
      <UserManagerProvider userManager={userManager}>
        <ThemeProvider theme={theme({ ...myTheme, height: prevHeight })}>
          <BrowserRouter basename="/booking">
            <SetBackUrl />
            <div style={{ height: "100%" }}>
              <Switch>
                <Route
                  path="/callback"
                  render={() => <CallbackPage {...props} />}
                />
                <Route path="/silent_renew" component={SilentRenew} />
                <Route path="/" render={() => <Content {...props} />} />
              </Switch>
            </div>
          </BrowserRouter>
        </ThemeProvider>
      </UserManagerProvider>
    </AppContextProvider>
  );
};

export default Routes;
